import { Point } from '@hcie/shared';

/**
 * Simplifies a polyline using the Ramer-Douglas-Peucker algorithm.
 * @param points The array of points to simplify.
 * @param tolerance The distance tolerance for simplification.
 * @returns The simplified array of points.
 */
export function simplifyPath(points: Point[], tolerance: number): Point[] {
    if (points.length <= 2) return points;

    const sqTolerance = tolerance * tolerance;
    let simplified: Point[] = [points[0]];
    
    function simplifyRecursive(start: number, end: number) {
        let maxSqDist = 0;
        let index = -1;

        for (let i = start + 1; i < end; i++) {
            const sqDist = getSqSegDist(points[i], points[start], points[end]);
            if (sqDist > maxSqDist) {
                index = i;
                maxSqDist = sqDist;
            }
        }

        if (maxSqDist > sqTolerance) {
            simplifyRecursive(start, index);
            simplified.push(points[index]);
            simplifyRecursive(index, end);
        }
    }

    simplifyRecursive(0, points.length - 1);
    simplified.push(points[points.length - 1]);

    return simplified;
}

/**
 * Helper to get square distance from a point to a segment.
 */
function getSqSegDist(p: Point, p1: Point, p2: Point): number {
    let x = p1.x, y = p1.y;
    let dx = p2.x - x, dy = p2.y - y;

    if (dx !== 0 || dy !== 0) {
        let t = ((p.x - x) * dx + (p.y - y) * dy) / (dx * dx + dy * dy);
        if (t > 1) {
            x = p2.x; y = p2.y;
        } else if (t > 0) {
            x += dx * t; y += dy * t;
        }
    }

    dx = p.x - x;
    dy = p.y - y;
    return dx * dx + dy * dy;
}
